import { ReactNode } from 'react';
import { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import {
  getEntityCounters,
  getIconColor,
  getIconType,
  getSortedCalendars
} from 'components/mrp/mrp.utils';
import { CalendarDisplayData } from 'components/mrp/stores/calendarStore/calendarStore.types';
import { FormattedTreatment } from './CalendarHeader.types';
import { TreatmentResponse } from 'generated/index';
import i18n from 'config/i18n';
import { formatDate, getLocale, fromISOString } from 'utils/date';
import { startOfDay, endOfDay } from 'date-fns';

const t = i18n.getFixedT(getLocale(), 'common', 'mrp');

const getCalendarsToTheRight = ({
  calendarId,
  calendars
}: {
  calendarId: string;
  calendars: { [calendarId: string]: CalendarDisplayData };
}): CalendarDisplayData[] => {
  const calendarList = getSortedCalendars(calendars);
  const currentCalendar =
    calendarList.filter(({ calendarId: srcId }) => srcId == calendarId)[0] ||
    null;

  const currentCalendarIndex = calendarList.indexOf(currentCalendar);
  if (currentCalendarIndex == -1) {
    return [];
  }

  const calendarIdsToTheRight = calendarList.slice(currentCalendarIndex + 1);

  return calendarIdsToTheRight;
};

export const getCloseActions = ({
  calendars,
  closeCalendar,
  displayData
}: {
  calendars: { [calendarId: string]: CalendarDisplayData };
  closeCalendar: (calendarId: string, forceClose?: boolean) => void;
  displayData: CalendarDisplayData;
}): ContextMenuAction => {
  return {
    value: t('close'),
    subActions: [
      {
        value: t('closeAllCalendars'),
        onClick: () => {
          Object.keys(calendars).forEach((calendarId) => {
            closeCalendar(calendarId, true);
          });
        }
      },
      {
        value: t('closeOtherCalendars'),
        disabled: Object.keys(calendars).length < 2,
        onClick: () => {
          Object.keys(calendars)
            .filter((calendarId) => calendarId != displayData.calendarId)
            .forEach((calendarId) => {
              closeCalendar(calendarId, true);
            });
        }
      },
      {
        value: t('closeCalendarsToTheRight'),
        disabled:
          getCalendarsToTheRight({
            calendarId: displayData.calendarId,
            calendars
          }).length == 0,
        onClick: () => {
          const calendarsToTheRight = getCalendarsToTheRight({
            calendarId: displayData.calendarId,
            calendars
          });

          calendarsToTheRight.forEach(({ calendarId }) => {
            closeCalendar(calendarId, true);
          });
        }
      }
    ]
  } as ContextMenuAction;
};

export const getMergeActions = ({
  calendars,
  mergeCalendars,
  displayData
}: {
  calendars: { [calendarId: string]: CalendarDisplayData };
  mergeCalendars: (calendarIds: string[]) => void;
  displayData: CalendarDisplayData;
}): ContextMenuAction => {
  return {
    value: t('merge'),
    subActions: [
      {
        value: t('mergeAll'),
        disabled: Object.keys(calendars).length < 2,
        onClick: () => {
          mergeCalendars([
            displayData.calendarId,
            ...Object.keys(calendars).filter(
              (calendarId) => calendarId != displayData.calendarId
            )
          ]);
        }
      },
      {
        value: t('mergeToTheRight'),
        disabled:
          getCalendarsToTheRight({
            calendarId: displayData.calendarId,
            calendars
          }).length == 0,
        onClick: () => {
          mergeCalendars([
            displayData.calendarId,
            ...getCalendarsToTheRight({
              calendarId: displayData.calendarId,
              calendars
            }).map(({ calendarId }) => calendarId)
          ]);
        }
      },
      ...Object.values(calendars)
        .filter(({ calendarId }) => displayData.calendarId != calendarId)
        .map(({ sources, calendarId }) => {
          const iconEntityType =
            sources.length > 1
              ? 'MIX'
              : sources[0].planningEntityIdentifier.planningEntityType;

          const iconType = getIconType(iconEntityType);
          const iconColor = getIconColor(iconEntityType);

          const planningEntityCounters = getEntityCounters(
            sources.map((x) => x.planningEntityIdentifier.planningEntityType)
          );

          return {
            value: (
              <>
                {sources.length == 1 ? sources[0].label : t('mergedCalendar')}
                {sources.length > 1 && planningEntityCounters}
              </>
            ),
            onClick: () => {
              mergeCalendars([displayData.calendarId, calendarId]);
            },
            icon: {
              iconColor,
              iconType
            }
          } satisfies ContextMenuAction;
        })
    ]
  };
};

export const getSplitActions = ({
  splitCalendar,
  displayData
}: {
  splitCalendar: (calendarId: string, entityId?: string) => void;
  displayData: CalendarDisplayData;
}): ContextMenuAction => {
  return {
    value: t('split'),
    subActions: [
      {
        value: t('splitAll'),
        onClick: () => {
          splitCalendar(displayData.calendarId);
        }
      },
      ...displayData.sources.map(({ planningEntityIdentifier, label }) => {
        const iconEntityType = planningEntityIdentifier.planningEntityType;

        const iconType = getIconType(iconEntityType);
        const iconColor = getIconColor(iconEntityType);

        return {
          value: label,
          onClick: () => {
            splitCalendar(
              displayData.calendarId,
              planningEntityIdentifier.entityId
            );
          },
          icon: {
            iconColor,
            iconType
          }
        } satisfies ContextMenuAction;
      })
    ]
  };
};

export const getMoveActions = ({
  calendars,
  moveCalendar,
  displayData
}: {
  calendars: { [calendarId: string]: CalendarDisplayData };
  moveCalendar: (
    calendarId: string,
    newPosition: 1 | -1 | 'MIN' | 'MAX'
  ) => void;
  displayData: CalendarDisplayData;
}): ContextMenuAction[] => {
  const sortedCalendars = getSortedCalendars(calendars);

  const isLeftMostOrNextToMergedCalender = () => {
    const currentIdx = sortedCalendars.indexOf(
      calendars[displayData.calendarId]
    );
    if (currentIdx === 0) return true;

    const adjacentIsMerged = sortedCalendars[currentIdx - 1].sources.length > 1;

    return adjacentIsMerged;
  };

  const isRightMost = () => {
    return (
      sortedCalendars.indexOf(calendars[displayData.calendarId]) ===
      sortedCalendars.length - 1
    );
  };

  return [
    {
      value: t('calendarSorting.pullLeft'),
      icon: {
        iconType: 'arrow-left-to-line'
      },
      onClick: () => {
        moveCalendar(displayData.calendarId, 'MAX');
      },
      disabled: isLeftMostOrNextToMergedCalender()
    },
    {
      value: t('calendarSorting.left'),
      icon: {
        iconType: 'arrow-left'
      },
      onClick: () => {
        moveCalendar(displayData.calendarId, -1);
      },
      disabled: isLeftMostOrNextToMergedCalender()
    },
    {
      value: t('calendarSorting.right'),
      icon: {
        iconType: 'arrow-right'
      },
      onClick: () => {
        moveCalendar(displayData.calendarId, 1);
      },
      disabled: isRightMost()
    },
    {
      value: t('calendarSorting.pullRight'),
      icon: {
        iconType: 'arrow-right-to-line'
      },
      onClick: () => {
        moveCalendar(displayData.calendarId, 'MIN');
      },
      disabled: isRightMost()
    }
  ];
};

const getPrioritizedDate = (
  actual?: string | null,
  planned?: string | null,
  expected?: string | null,
  fallback?: string | null
): string | null => {
  return actual || planned || expected || fallback || null;
};

export const getFormattedTreatments = (
  treatments: TreatmentResponse[]
): FormattedTreatment[] => {
  if (!treatments) return [];

  const todayStart = startOfDay(new Date());
  const todayEnd = endOfDay(new Date());

  return treatments.reduce<FormattedTreatment[]>((acc, treatment) => {
    const startDate = getPrioritizedDate(
      treatment.actualStartDate,
      treatment.plannedStartDate,
      treatment.expectedStartDate,
      treatment.actualOrPlannedOrExpectedStartDate
    );
    const endDate = getPrioritizedDate(
      treatment.actualEndDate,
      treatment.plannedEndDate,
      treatment.expectedEndDate,
      treatment.actualOrPlannedOrExpectedEndDate
    );

    const hasStarted = !startDate || fromISOString(startDate) <= todayEnd;

    const hasNotEnded = !endDate || fromISOString(endDate) >= todayStart;

    // Treatment is active if it has started and has not ended
    if (!hasStarted || !hasNotEnded) {
      return acc;
    }

    const untilAndIncluding = t('entityInfo.untilAndIncluding');
    const dateRange =
      startDate && endDate
        ? `${formatDate(startDate)} ${untilAndIncluding} ${formatDate(endDate)}`
        : startDate
        ? formatDate(startDate)
        : endDate
        ? `${untilAndIncluding} ${formatDate(endDate)}`
        : null;

    acc.push({
      departmentName: treatment.departmentName,
      groupName: treatment.groupName,
      dateRange
    });

    return acc;
  }, []);
};

export const formatTreatments = (
  treatments: FormattedTreatment[],
  dividerColor: string
): ReactNode => {
  if (!treatments || treatments.length === 0) return null;

  return (
    <>
      {treatments.map((treatment, index) => (
        <div key={index}>
          <div>{treatment.departmentName}</div>
          <div>{treatment.groupName}</div>
          {treatment.dateRange && <div>{treatment.dateRange}</div>}
          {index < treatments.length - 1 && (
            <hr
              style={{
                margin: '8px 0',
                border: 'none',
                borderTop: `1px solid ${dividerColor}`
              }}
            />
          )}
        </div>
      ))}
    </>
  );
};
